import { twMerge } from '@/lib/classname/twMerge'
import { Title } from '@/ui/text/title/Title'
import type React from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import type { SectionLinkProps } from './SectionLink.client'
import { SectionLink } from './SectionLink.client'

export type SectionProps = PropsWithChildren<{
  /** The title of the Section */
  title: string | ReactNode | null | undefined

  /** A place to display UI on top right side */
  topRightOutlet?: React.ReactNode

  /** The text directly below the title */
  subTitle?: string

  /** The link on the right of the title, his text and the associated link */
  link?: Omit<SectionLinkProps, 'className'>

  /** The classNames to add, if there is any */
  className?: string

  /** The classNames to add to the top right outlet, if there is any */
  topRightOutletClassName?: React.ReactNode

  /** The icon to display on the left of the title */
  icon?: React.ReactNode
}>

export function Section({
  children,
  title,
  subTitle,
  link,
  topRightOutlet,
  topRightOutletClassName,
  icon,
  className = '',
}: SectionProps): React.ReactElement {
  return (
    <section data-testid="section" className={twMerge('py-4', className)}>
      {(!!title || subTitle || link) && (
        <div className="mb-3 flex w-full flex-col justify-between gap-1 gap-y-2 desktop:flex-row desktop:items-center desktop:gap-y-0">
          <div className="space-y-1">
            <div className="flex items-center gap-0.5">
              {!!icon && <div data-testid="button/icon">{icon}</div>}
              {!!title && (
                <Title as="h2" className="font-bold" size="3xl">
                  {title}
                </Title>
              )}
            </div>
            {!!subTitle && <p data-testid="section/sub-title">{subTitle}</p>}
          </div>
          {link ? (
            <SectionLink
              className="ml-3 hidden shrink-0 desktop:block desktop:self-baseline"
              href={link.href}
              name={link.name}
              opensInNewTab={link.opensInNewTab}
              analytics={link.analytics}
            />
          ) : topRightOutlet ? (
            <div className={twMerge(topRightOutletClassName, 'desktop:self-center')}>
              {topRightOutlet}
            </div>
          ) : null}
        </div>
      )}
      {children}
      {link && <SectionLink className="mt-3 block text-center desktop:hidden" {...link} />}
    </section>
  )
}
